<template>
  <v-container>
    <v-container>
      <v-card>
        <v-card-title>
          {{ $t("navCertificates") }}
          <v-btn
              v-if="checkPermission('accounts.anvio.certificate.create')"
              icon
              @click.stop="showCertificatesCreateModal = true"
          >
            <v-icon>mdi-plus-circle</v-icon>
          </v-btn>
          <certificates-create-modal
              :certificatesCreateModal="showCertificatesCreateModal"
              :uniqueCode="uniqueCode"
              @reload-certificates="uploadCertificates"
              @close-dialog="showCertificatesCreateModal = false"
          ></certificates-create-modal>
        </v-card-title>
        <v-card-text>
          <v-container class="py-5 px-3">
            <v-row>
              <v-col cols="12" sm="2" class="py-0">
                <v-menu
                  v-model="datepickerFrom"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="
                        moment(selectedFilters.date_from).format('DD.MM.YYYY')
                      "
                      :label="$t('paymentsFilterStart')"
                      type="text"
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      prepend-icon="mdi-calendar-start"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    :max="moment.parseZone().format('YYYY-MM-DD')"
                    v-model="selectedFilters.date_from"
                    no-title
                    scrollable
                    :locale="$i18n.locale"
                    @change="handleDateChange"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="2" class="py-0">
                <v-menu
                  v-model="datepickerTo"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="
                        moment(selectedFilters.date_to).format('DD.MM.YYYY')
                      "
                      :label="$t('paymentsFilterEnd')"
                      type="text"
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      prepend-icon="mdi-calendar-end"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    :min="
                      moment
                        .parseZone(selectedFilters.date_from)
                        .format('YYYY-MM-DD')
                    "
                    :max="moment.parseZone().format('YYYY-MM-DD')"
                    v-model="selectedFilters.date_to"
                    no-title
                    scrollable
                    :locale="$i18n.locale"
                    @change="handleDateChange"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="2" class="py-0">
                <v-select
                  :items="certificateStatuses"
                  item-text="title"
                  item-value="slug"
                  v-model="selectedStatuses"
                  :label="$t('certificatesStatus')"
                  multiple
                  dense
                  @change="handleStatusChange"
                >
                  <template slot="no-data">
                    <span class="grey--text caption ml-1">{{
                      $t("placeholderLoading")
                    }}</span>
                  </template>
                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0">{{ item.title }}</span>
                    <span v-if="index === 1" class="grey--text caption ml-1">
                      (+{{ selectedStatuses.length - 1 }})
                    </span>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-action>
                      <v-checkbox
                        :input-value="selectedStatuses.includes(item.slug)"
                      ></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="2" class="py-0">
                <v-text-field
                    v-model="selectedFilters.search"
                    :label="$t('certificatesSearch')"
                    single-line
                    dense
                >
                  <template v-slot:prepend>
                    <v-icon @click="resetSearch">mdi-close-circle</v-icon>
                  </template>
                  <template v-slot:append>
                    <v-icon @click="handleSearch">mdi-magnify</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col class="py-0 col-auto ml-auto">
                <v-btn
                  @click="uploadCertificates((csv = true))"
                  outlined
                  color="green darken-1"
                  text
                  tile
                  :loading="loadingCSV"
                  :disabled="certificates == null || certificates == [] || loadingCSV"
                  ><v-icon left>mdi-file-export</v-icon>CSV</v-btn
                >
              </v-col>
            </v-row>
          </v-container>
          <v-data-table
            v-if="certificates && !isLoading"
            :headers="headers"
            :items="certificates"
            :items-per-page="20"
            class="elevation-1"
            :no-data-text="$t('certificatesNotFound')"
            :footer-props="{
              'items-per-page-options': limits,
              'items-per-page-text': $t('paymentsRowsPerPage'),
            }"
            :server-items-length="totalPages"
            :page.sync="currentPage"
            :items-per-page.sync="selectedFilters.limit"
            @update:page="onPageUpdate"
            @update:items-per-page="onItemsPerPageUpdate"
          >
            <template v-slot:item.rate="{ item }">
              <span v-html="convertPrice(item.currency, item.rate)"></span>
            </template>
            <template v-slot:item.remaining_rate="{ item }">
              <span v-html="convertPrice(item.currency, item.remaining_rate)"></span>
            </template>
            <template v-slot:item.status="{ item }">
              <span>{{ findCertificateStatus(item.status) }}</span>
            </template>
            <template v-slot:item.created_by="{ item }">
              <span v-if="usersData[item.created_by]">
                {{ usersData[item.created_by].email }}
              </span>
            </template>
            <template v-slot:item.datetime_created="{ item }">
              <span>{{ item.datetime_created | dateAndTimeFilter }}</span>
            </template>
            <template v-slot:item.seller_user="{ item }">
              <span v-if="usersData[item.seller_user]">
                {{ usersData[item.seller_user].email }}
              </span>
            </template>
            <template v-slot:item.datetime_activated="{ item }">
              <span>{{ item.datetime_activated | dateAndTimeFilter }}</span>
            </template>
            <template v-slot:item.booked_phone="{ item }">
              <span v-if="usersData[item.booked_by]">
                {{ usersData[item.booked_by].phone }}
              </span>
            </template>
            <template v-slot:item.booked_email="{ item }">
              <span v-if="usersData[item.booked_by]">
                {{ usersData[item.booked_by].email }}
              </span>
            </template>
            <template v-slot:item.action="{ item }">
              <v-btn
                  v-if="item.status === 'NOT_PAID' && checkPermission('accounts.anvio.certificate.activate')"
                  color="primary"
                  small
                  @click="openDialog(item)"
              >
                {{ $t('certificatesActivate') }}
              </v-btn>

              <v-btn
                  v-else-if="item.status === 'ACTIVE' && checkPermission('accounts.anvio.certificate.activate')"
                  color="primary"
                  small
                  :disabled="item.sending"
                  @click="handleSendLetter(item)"
              >
                {{ $t('certificatesSendBtn') }}
              </v-btn>

              <span v-else-if="item.status === 'USED' && checkPermission('accounts.anvio.certificate.activate')">
                {{ $t('certificatesUsed') }}
              </span>
            </template>
          </v-data-table>
          <v-skeleton-loader
            v-else
            class="mx-n3 mt-n4"
            type="table-thead, table-tbody, table-tfoot"
            tile
          ></v-skeleton-loader>
        </v-card-text>
      </v-card>
      <certificates-activate-modal
          :certificatesActivateModal="showCertificatesActivateModal"
          :uniqueCode="uniqueCode"
          @reload-certificates="uploadCertificates"
          @close-dialog="showCertificatesActivateModal = false"
      ></certificates-activate-modal>
    </v-container>
  </v-container>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import moment from "moment/moment";
import store from "@/store";
import CertificatesActivateModal from "@/components/CertificatesActivateModal.vue";
import CertificatesCreateModal from "@/components/CertificatesCreateModal.vue";

export default {
  name: "LocationCertificates",
  components: { CertificatesActivateModal, CertificatesCreateModal },

  beforeRouteEnter: async (to, from, next) => {
    await store.dispatch("loadLocation", to.params.location_id);
    next();
  },

  data() {
    return {
      certificates: [],
      headers: [
        {
          text: this.$i18n.t("certificatesUniqueNumber"),
          value: "unique_number",
          width: "5%",
          sortable: false,
          align: "center",
        },
        {
          text: this.$i18n.t("certificatesRate"),
          value: "rate",
          width: "5%",
          sortable: false,
          align: "center",
        },
        {
          text: this.$i18n.t("certificatesRemainingRate"),
          value: "remaining_rate",
          width: "5%",
          sortable: false,
          align: "center",
        },
        {
          text: this.$i18n.t("groupType"),
          value: "type",
          width: "5%",
          sortable: false,
          align: "center",
        },
        {
          text: this.$i18n.t("certificatesDesign"),
          value: "design",
          width: "5%",
          sortable: false,
          align: "center",
        },
        {
          text: this.$i18n.t("certificatesStatus"),
          value: "status",
          width: "5%",
          sortable: false,
          align: "center",
        },
        {
          text: this.$i18n.t("certificatesCreatedBy"),
          value: "created_by",
          width: "5%",
          sortable: false,
          align: "center",
        },
        {
          text: this.$i18n.t("certificatesDatetimeCreated"),
          value: "datetime_created",
          width: "5%",
          sortable: false,
          align: "center",
        },
        {
          text: this.$i18n.t("certificatesSellerUser"),
          value: "seller_user",
          width: "5%",
          sortable: false,
          align: "center",
        },
        {
          text: this.$i18n.t("certificatesDatetimeActivated"),
          value: "datetime_activated",
          width: "5%",
          sortable: false,
          align: "center",
        },
        {
          text: this.$i18n.t("certificatesBookedPhone"),
          value: "booked_phone",
          width: "5%",
          sortable: false,
          align: "center",
        },
        {
          text: this.$i18n.t("certificatesBookedEmail"),
          value: "booked_email",
          width: "5%",
          sortable: false,
          align: "center",
        },
        {
          text: this.$i18n.t("certificatesActions"),
          value: "action",
          width: "5%",
          sortable: false,
          align: "center",
        },
      ],
      selectedStatuses: [],
      certificateStatuses: [
        {
          slug: 'NOT_PAID',
          title: this.$i18n.t("notPaid"),
        },
        {
          slug: 'ACTIVE',
          title: this.$i18n.t("active"),
        },
        {
          slug: 'EXPIRED',
          title: this.$i18n.t("expired"),
        },
        {
          slug: 'USED',
          title: this.$i18n.t("used"),
        },
      ],
      usersData: {},
      loadingCSV: false,
      certificatesCSV: null,
      datepickerFrom: false,
      datepickerTo: false,
      selectedFilters: {
        date_from: moment.parseZone().startOf("month").format("YYYY-MM-DD"),
        date_to: moment.parseZone().format("YYYY-MM-DD"),
        offset: 0,
        limit: 10,
        status: "",
        search: "",
      },
      totalPages: null,
      limits: [10, 15, 30, 50, 100],
      currentPage: 1,
      isLoading: false,
      showCertificatesActivateModal: false,
      showCertificatesCreateModal: false,
      uniqueCode: null
    };
  },
  mounted() {
    this.uploadCertificates();
    console.log(this.usersData)
  },
  computed: {
    ...mapGetters(["location", "locationCertificates", "staffUser", "checkPermission"]),
  },
  filters: {
    dateAndTimeFilter(value) {
      if (value) {
        return moment.parseZone(value).format("DD.MM.YYYY HH:mm");
      }
    },
  },
  methods: {
    ...mapActions(["loadLocationCertificates", "getCertificatesCSV", "loadStaffUser"]),
    async sendLoadLocationCertificatesRequest(payload) {
      this.certificates = null;
      await this.loadLocationCertificates(payload);
      if (this.locationCertificates.results) {
        const response = this.locationCertificates;
        this.certificates = response.results;
        this.totalPages = response.count;
        await this.preloadUserInformation();
      }
    },
    uploadCertificates(csv) {
      this.selectedFilters.offset =
        this.selectedFilters.limit * (this.currentPage - 1);
      const filteredFilters = Object.entries(this.selectedFilters).reduce(
        (a, [k, v]) =>
          typeof v === "object" && !v.length ? a : ((a[k] = v), a),
        {}
      );

      const payload = Object.keys(filteredFilters)
        .map((key) => key + "=" + filteredFilters[key])
        .join("&");

      if (csv) {
        this.loadingCSV = true;
        this.getCertificatesCSV(payload).then((response) => {
          if (response.data) {
            this.certificatesCSV = response.data;
            this.loadingCSV = false;
            this.downloadCSV();
          } else {
            this.certificatesCSV = null;
          }
        });
      } else {
        this.sendLoadLocationCertificatesRequest(payload);
      }
    },
    downloadCSV() {
      const anchor = document.createElement("a");
      anchor.href =
        "data:text/csv;charset=utf-8," + encodeURIComponent(this.certificatesCSV);
      anchor.target = "_blank";
      anchor.download = "certificates.csv";
      anchor.click();
    },
    async preloadUserInformation() {
      this.isLoading = true;
      const userIds = this.certificates.reduce((acc, certificate) => {
        if (certificate.seller_user && !acc.includes(certificate.seller_user)) {
          acc.push(certificate.seller_user);
        }
        if (certificate.created_by && !acc.includes(certificate.created_by)) {
          acc.push(certificate.created_by);
        }
        if (certificate.booked_by && !acc.includes(certificate.booked_by)) {
          acc.push(certificate.booked_by);
        }
        return acc;
      }, []);

      await Promise.all(
          userIds.map(async userId => {
            this.usersData[userId] = await this.loadStaffUser(userId);
          })
      );
      this.isLoading = false;
    },
    findCertificateStatus(statusSlug) {
      const status = this.certificateStatuses.find((s) => s.slug === statusSlug);
      return status ? status.title : `ID: ${statusSlug}`;
    },
    convertPrice(currency, price) {
      if (currency.is_before) {
        return currency.code + currency.blank_place + price;
      } else {
        return price + currency.blank_place + currency.code;
      }
    },
    onPageUpdate(newPage) {
      this.currentPage = newPage;
      this.uploadCertificates();
    },
    onItemsPerPageUpdate(newLimit) {
      this.selectedFilters.limit = newLimit;
      this.uploadCertificates();
    },
    handleDateChange() {
      this.selectedPeriod = "";
      this.uploadCertificates();
    },
    handleStatusChange() {
      this.selectedFilters.status = this.selectedStatuses.join(",");
      this.uploadCertificates();
    },
    async handleSearch() {
      if (this.selectedFilters.search) {
        this.currentPage = 1
        await this.uploadCertificates();
      }
    },
    resetSearch() {
      if (this.selectedFilters.search) {
        this.selectedFilters.search = '';
        this.currentPage = 1
        this.uploadCertificates();
      }
    },
    openDialog(item) {
      this.uniqueCode = item.unique_number
      this.showCertificatesActivateModal = true;
    },
    async handleSendLetter(item) {
      this.$set(item, 'sending', true);

      await this.$store.dispatch('certificateResendLetter', {
        code: item.unique_number,
        payload: { location_id: item.creating_location },
        i18n: this.$i18n
      });
    },
  },
};
</script>
